import { useCallback, useContext, useEffect, useRef } from "react";

import { StateContext } from "../../context/globalContext/context";
import { iGlobalContext } from "../../context/globalContext/interface";
import { API_METHODS } from "../../enums";
import { fetchApi } from "../../utils/apiFunction";
import { getSessionId } from "../../utils/sessionId";
import { pushPendingEvent } from "./pendingTheresiasQueue";

export async function postIntentEvent(
  accessJWT: string,
  userId: string,
  event_name: string,
  custom?: Record<string, unknown>,
  context?: Record<string, unknown>
): Promise<void> {
  const session_id = getSessionId();
  const body: Record<string, unknown> = { session_id, userId, event_name };
  if (custom) body.custom = custom;
  if (context) body.context = context;
  try {
    await fetchApi(accessJWT, "intent/event", API_METHODS.POST, body);
  } catch {
    // swallow — fire-and-forget
  }
}

export function useFireIntentEventOnMount(
  eventName: string,
  when: boolean = true,
  custom?: Record<string, unknown>,
  context?: Record<string, unknown>
): void {
  const { accessJWT, userInfo } = useContext(StateContext) as iGlobalContext;
  const firedRef = useRef(false);
  useEffect(() => {
    if (firedRef.current) return;
    if (!when) return;
    if (!accessJWT || !userInfo?.id) return;
    firedRef.current = true;
    postIntentEvent(accessJWT, String(userInfo.id), eventName, custom, context);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessJWT, userInfo?.id, when]);
}

export function useIntentEventFirer(): (
  event_name: string,
  custom?: Record<string, unknown>,
  context?: Record<string, unknown>
) => void {
  const { accessJWT, userInfo } = useContext(StateContext) as iGlobalContext;
  const jwtRef = useRef(accessJWT);
  jwtRef.current = accessJWT;
  const uidRef = useRef(userInfo?.id);
  uidRef.current = userInfo?.id;
  return useCallback(
    (event_name: string, custom?: Record<string, unknown>, context?: Record<string, unknown>) => {
      const jwt = jwtRef.current;
      const uid = uidRef.current;
      if (!jwt || !uid) {
        // Pre-login fire — onboarding events (budget / travel / vibe /
        // home-city) fire before signup completes, so `userInfo.id` and
        // `accessJWT` are empty. Buffer to localStorage; `useFlushPendingTheresias`
        // (mounted at App root) drains + re-fires once login lands. Original
        // `context.ts` timestamp preserved so Theresias sees true event-time.
        pushPendingEvent({ event_name, custom, context });
        return;
      }
      postIntentEvent(jwt, String(uid), event_name, custom, context);
    },
    []
  );
}

export function useFireScreenMountUnmount(
  screen: string,
  when: boolean,
  custom?: Record<string, unknown>,
  context?: Record<string, unknown>
): void {
  const { accessJWT, userInfo } = useContext(StateContext) as iGlobalContext;
  const jwtRef = useRef(accessJWT);
  jwtRef.current = accessJWT;
  const uidRef = useRef(userInfo?.id);
  uidRef.current = userInfo?.id;

  useEffect(() => {
    if (!when || !accessJWT || !userInfo?.id) return;
    const merged = { ...(custom ?? {}), screen };
    postIntentEvent(accessJWT, String(userInfo.id), "screen-mount", merged, context);
    return () => {
      const jwt = jwtRef.current;
      const uid = uidRef.current;
      if (!jwt || !uid) return;
      postIntentEvent(jwt, String(uid), "screen-unmount", merged, context);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [when, accessJWT, userInfo?.id, screen]);
}
